import { Crown, MoreHorizontal, Tag, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Input,
  Popover,
  PopoverTrigger,
  ScrollArea,
} from "@link/ui";
import { DEFAULT_COMMUNITY_PERMISSIONS, ROLE_PERMISSIONS } from "@link/shared";

import {
  api,
  type CommunityTagPreview,
  type SpaceMember,
  type SpaceRole,
} from "../lib/api";
import { knownBadges, verifiedBadgeOnly } from "../lib/badges";
import { highestMemberRole, memberProfile } from "../lib/member-presentation";
import { RoleName } from "./RoleName";
import { MemberContextMenu, MemberNicknameDialog, type MemberMenuAnchor } from "./MemberContextMenu";
import { UserProfileModal } from "./UserProfileModal";
import { useRealtimeSubscription } from "../state/Realtime";
import { useSelf } from "../state/SelfSession";
import type { SpaceView } from "../types/ui";
import { BadgeRow } from "./BadgeRow";
import { MemberProfilePopover } from "./MemberProfilePopover";
import { StatusDot } from "./StatusDot";

interface Props {
  space: SpaceView;
}

export function MemberList({ space }: Props) {
  const self = useSelf();
  const [members, setMembers] = useState<SpaceMember[]>([]);
  const [roles, setRoles] = useState<SpaceRole[]>([]);
  const [defaultPermissions, setDefaultPermissions] = useState(DEFAULT_COMMUNITY_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState<MemberMenuAnchor | null>(null);
  const [profileXid, setProfileXid] = useState<string | null>(null);
  const [nicknameXid, setNicknameXid] = useState<string | null>(null);
  const profileMember = members.find((member) => member.account_id === profileXid);
  const nicknameMember = members.find((member) => member.account_id === nicknameXid);
  const contextMember = members.find((member) => member.account_id === context?.accountXid);
  const [tagPreview, setTagPreview] = useState<CommunityTagPreview | null>(null);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [tagCode, setTagCode] = useState("");
  const [tagLabel, setTagLabel] = useState("");
  const refreshRequest = useRef(0);

  const refresh = useCallback((showLoading = true): void => {
    const request = ++refreshRequest.current;
    if (showLoading) setLoading(true);
    void Promise.all([
      api.spaces.members(space.xid),
      space.spaceType === "community"
        ? api.spaces.roles(space.xid)
        : Promise.resolve({ roles: [] as SpaceRole[], default_permissions: 0 }),
    ])
      .then(([memberResponse, roleResponse]) => {
        if (request !== refreshRequest.current) return;
        setMembers(memberResponse.members);
        setRoles(roleResponse.roles);
        setDefaultPermissions(roleResponse.default_permissions);
      })
      .catch(() => {
        if (request !== refreshRequest.current) return;
        if (showLoading) {
          setMembers([]);
          setRoles([]);
          setDefaultPermissions(space.spaceType === "community" ? DEFAULT_COMMUNITY_PERMISSIONS : 0);
        }
      })
      .finally(() => {
        if (request === refreshRequest.current) setLoading(false);
      });
  }, [space.spaceType, space.xid]);

  useEffect(() => { setMembers([]); setRoles([]); refresh(); return () => { refreshRequest.current += 1; }; }, [refresh]);
  useEffect(() => {
    const refreshQuietly = (): void => refresh(false);
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "visible") refreshQuietly();
    };
    const interval = window.setInterval(refreshQuietly, 60_000);
    window.addEventListener("focus", refreshQuietly);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshQuietly);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refresh]);
  useEffect(() => { setContext(null); setProfileXid(null); setNicknameXid(null); }, [space.xid]);

  const selfMember = members.find(
    (member) => member.account_id === self.identity.accountXid,
  );
  const selfPermissions = selfMember?.is_owner === true || selfMember?.is_admin === true
    ? Number.MAX_SAFE_INTEGER
    : roles.reduce(
        (mask, role) => selfMember?.roles.some((assigned) => assigned.xid === role.xid) === true ? mask | role.permissions : mask,
        defaultPermissions,
      );
  const canManageTag = (selfPermissions & ROLE_PERMISSIONS.manage_server) !== 0;

  useRealtimeSubscription([`space:${space.xid}`], (frame) => {
    if (
      frame.type === "space.member_added"
      || frame.type === "space.member_removed"
      || frame.type === "space.member_updated"
      || frame.type === "space.role_created"
      || frame.type === "space.role_updated"
      || frame.type === "space.role_deleted"
      || frame.type === "space.member_roles_updated"
      || frame.type === "space.member_banned"
      || frame.type === "space.member_unbanned"
      || frame.type === "community_tag.updated"
    ) {
      refresh(false);
      return;
    }
    if (frame.type === "profile.updated") {
      const payload = frame.payload as {
        account_xid?: unknown;
        username?: unknown;
        display_name?: unknown;
        avatar_url?: unknown;
        banner_color?: unknown;
        banner_mode?: unknown;
        banner_secondary_color?: unknown;
        pronouns?: unknown;
        badges?: unknown;
      };
      if (typeof payload.account_xid !== "string") return;
      setMembers((current) =>
        current.map((member) => {
          if (member.account_id !== payload.account_xid) return member;
          return {
            ...member,
            ...(typeof payload.username === "string"
              ? { username: payload.username }
              : {}),
            ...(typeof payload.display_name === "string" || payload.display_name === null
              ? { display_name: payload.display_name }
              : {}),
            ...(typeof payload.avatar_url === "string" || payload.avatar_url === null
              ? { avatar_url: payload.avatar_url }
              : {}),
            ...(typeof payload.banner_color === "string"
              ? { banner_color: payload.banner_color }
              : {}),
            ...(payload.banner_mode === "solid" || payload.banner_mode === "gradient"
              ? { banner_mode: payload.banner_mode }
              : {}),
            ...(typeof payload.banner_secondary_color === "string"
              ? { banner_secondary_color: payload.banner_secondary_color }
              : {}),
            ...(typeof payload.pronouns === "string" || payload.pronouns === null
              ? { pronouns: payload.pronouns }
              : {}),
            ...(Array.isArray(payload.badges)
              ? {
                  badges: knownBadges(
                    payload.badges.filter(
                      (badge): badge is string => typeof badge === "string",
                    ),
                  ),
                }
              : {}),
          };
        }),
      );
      return;
    }
    if (frame.type !== "presence.online" && frame.type !== "presence.offline") {
      return;
    }
    const payload = frame.payload as { account_xid?: unknown; presence?: unknown };
    if (typeof payload.account_xid !== "string") return;
    const presence = payload.presence === "online"
      || payload.presence === "away"
      || payload.presence === "busy"
      || payload.presence === "offline"
      ? payload.presence
      : frame.type === "presence.online" ? "online" : "offline";
    setMembers((current) =>
      current.map((member) =>
        member.account_id === payload.account_xid
          ? { ...member, presence }
          : member,
      ),
    );
  });

  const groups = useMemo(() => {
    const orderedRoles = [...roles].sort(
      (left, right) => left.sort_position - right.sort_position,
    );
    const grouped = new Map<string, SpaceMember[]>([
      ...orderedRoles.map((role) => [role.name, []] as [string, SpaceMember[]]),
      ["Members", []],
      ["Offline", []],
    ]);
    for (const member of members) {
      const highestRole = orderedRoles.find((role) =>
        member.roles.some((assigned) => assigned.xid === role.xid),
      );
      const heading =
        member.presence === "offline"
          ? "Offline"
          : highestRole?.name ?? "Members";
      grouped.get(heading)?.push(member);
    }
    return new Map(
      Array.from(grouped.entries()).filter(([, rows]) => rows.length > 0),
    );
  }, [members, roles]);

  const openTag = (tagXid: string): void => {
    void api.communityTags.preview(tagXid).then(setTagPreview).catch(() => undefined);
  };

  return (
    <aside className="glass-panel flex h-full w-full flex-col border-l border-cloud-200 bg-cloud-50 xl:w-64">
      <header className="flex items-center gap-2 border-b border-cloud-200 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-cloud-800">Members</p>
          <p className="text-[11px] text-cloud-400">
            {loading ? "Loading…" : `${members.length} in this space`}
          </p>
        </div>
        {canManageTag && space.spaceType === "community" ? (
          <button
            type="button"
            onClick={() => setTagManagerOpen(true)}
            className="rounded-lg p-2 text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-700"
            aria-label="Manage community tag"
          >
            <Tag className="h-4 w-4" />
          </button>
        ) : null}
      </header>

      <ScrollArea className="flex-1">
        <div className="space-y-4 px-3 py-3">
          {!loading && members.length === 0 ? (
            <p className="px-2 py-8 text-center text-xs text-cloud-500">
              No member data is available.
            </p>
          ) : null}
          {Array.from(groups.entries()).map(([role, rows]) => (
            <section key={role}>
              <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-cloud-500">
                {role} — {rows.length}
              </p>
              <ul className="space-y-0.5">
                {rows.map((member) => {
                  const badges = knownBadges(member.badges);
                  const inlineBadges = verifiedBadgeOnly(badges);
                  const nameRole = highestMemberRole(member, roles);
                  return (
                    <li key={member.account_id} className="group/member flex items-center gap-0.5">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            onContextMenu={(event) => {
                              event.preventDefault();
                              setContext({ accountXid: member.account_id, x: event.clientX, y: event.clientY, trigger: event.currentTarget });
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "ContextMenu" || (event.shiftKey && event.key === "F10")) {
                                event.preventDefault();
                                const rect = event.currentTarget.getBoundingClientRect();
                                setContext({accountXid:member.account_id,x:rect.left,y:rect.bottom,trigger:event.currentTarget});
                              }
                            }}
                            className="group flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-cloud-600 transition-colors hover:bg-strawberry-50 hover:text-strawberry-700"
                          >
                            <div className="relative flex-shrink-0">
                              <Avatar className="h-7 w-7">
                                {member.avatar_url !== null ? (
                                  <AvatarImage src={member.avatar_url} alt="" />
                                ) : null}
                                <AvatarFallback className="text-[11px]">
                                  {(member.nickname ?? member.display_name ?? member.username).slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <span className="absolute -bottom-0.5 -right-0.5">
                                <StatusDot status={member.presence} sizeClass="h-2.5 w-2.5" />
                              </span>
                            </div>
                            <span className="flex min-w-0 flex-1 items-center gap-1 truncate">
                              <RoleName name={member.nickname ?? member.display_name ?? member.username} {...(nameRole === undefined ? {} : {role: nameRole})} />
                              {member.is_owner ? <Crown className="h-3.5 w-3.5 flex-shrink-0 fill-amber-300 text-amber-500" aria-label="Server owner" /> : null}
                              <BadgeRow badges={inlineBadges} max={1} />
                              {member.community_tag !== null ? (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openTag(member.community_tag!.xid);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") openTag(member.community_tag!.xid);
                                  }}
                                  className="inline-flex flex-shrink-0 items-center rounded bg-grape-100 px-1.5 py-0.5 text-[9px] font-bold text-grape-700 ring-1 ring-grape-200"
                                  title={member.community_tag.label}
                                >
                                  {member.community_tag.code}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </PopoverTrigger>
                        <MemberProfilePopover
                          side="left"
                          align="start"
                          member={memberProfile(member, space.xid, space.name)}
                        />
                      </Popover>
                      <button type="button" aria-label={`Actions for ${member.username}`} className="shrink-0 rounded-md p-1 text-cloud-500 opacity-60 hover:bg-cloud-200 hover:opacity-100 focus-visible:opacity-100" onClick={(event) => {
                        const rect=event.currentTarget.getBoundingClientRect();
                        setContext({accountXid:member.account_id,x:rect.left,y:rect.bottom,trigger:event.currentTarget});
                      }}><MoreHorizontal className="h-3.5 w-3.5" /></button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </ScrollArea>

      {context !== null && contextMember !== undefined ? (
        <MemberContextMenu anchor={context} member={contextMember} actor={selfMember}
          roles={roles} defaults={defaultPermissions} spaceXid={space.xid}
          onClose={() => setContext(null)} onChanged={() => refresh(false)}
          onProfile={() => setProfileXid(contextMember.account_id)}
          onEditNickname={() => setNicknameXid(contextMember.account_id)} />
      ) : null}
      {profileMember !== undefined ? <UserProfileModal open member={memberProfile(profileMember, space.xid, space.name)} onOpenChange={(open) => {if (!open) setProfileXid(null);}} /> : null}
      {nicknameMember !== undefined ? <MemberNicknameDialog key={nicknameMember.account_id} member={nicknameMember} spaceXid={space.xid} onClose={() => setNicknameXid(null)} onChanged={() => refresh(false)} /> : null}

      {tagPreview !== null ? (
        <CommunityPreview
          preview={tagPreview}
          onClose={() => setTagPreview(null)}
          onChanged={() => {
            setTagPreview(null);
            refresh();
          }}
        />
      ) : null}

      {tagManagerOpen ? (
        <TagManager
          spaceXid={space.xid}
          code={tagCode}
          label={tagLabel}
          onCode={setTagCode}
          onLabel={setTagLabel}
          onClose={() => setTagManagerOpen(false)}
          onSaved={() => {
            setTagManagerOpen(false);
            setTagCode("");
            setTagLabel("");
            refresh();
          }}
        />
      ) : null}
    </aside>
  );
}

function TagManager({
  spaceXid,
  code,
  label,
  onCode,
  onLabel,
  onClose,
  onSaved,
}: {
  spaceXid: string;
  code: string;
  label: string;
  onCode: (value: string) => void;
  onLabel: (value: string) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void api.communityTags.save(spaceXid, { code, label }).then(onSaved);
        }}
        className="w-full max-w-sm rounded-2xl border border-grape-200 bg-white p-5 shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-cloud-800">Community tag</h2>
          <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <p className="mt-1 text-xs text-cloud-500">
          Members can feature this tag beside their name.
        </p>
        <Input value={code} onChange={(event) => onCode(event.target.value.toUpperCase())} maxLength={8} placeholder="TAG" className="mt-4" />
        <Input value={label} onChange={(event) => onLabel(event.target.value)} maxLength={32} placeholder="Community tag name" className="mt-2" />
        <Button type="submit" className="mt-4 w-full" disabled={code.length < 2 || label.length < 2}>
          Save tag
        </Button>
      </form>
    </div>
  );
}

function CommunityPreview({
  preview,
  onClose,
  onChanged,
}: {
  preview: CommunityTagPreview;
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-3xl border border-grape-300 bg-cloud-800 text-white shadow-2xl">
        <div className="h-28 bg-strawberry-gradient" />
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">{preview.space.name}</h2>
              <p className="mt-1 text-xs text-cloud-500">
                {preview.space.member_count.toLocaleString()} members · {preview.code}
              </p>
            </div>
            <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-cloud-600">
            {preview.space.description ?? preview.label}
          </p>
          {preview.is_member ? (
            <Button className="mt-5 w-full" onClick={() => void api.communityTags.equip(preview.xid).then(onChanged)}>
              Wear this tag
            </Button>
          ) : preview.space.discovery_enabled ? (
            <Button className="mt-5 w-full" onClick={() => void api.communityTags.join(preview.xid).then(onChanged)}>
              Join community
            </Button>
          ) : (
            <p className="mt-5 rounded-lg bg-white/10 px-3 py-2 text-center text-xs text-cloud-500">
              This community requires an invite.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
